import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "./Logo";

const servicesMenu = [
  {
    label: "Pool Maintenance Services",
    href: "/services/pool-maintenance",
  },
  {
    label: "Pool Openings & Closings",
    href: "/services/pool-openings-closings",
  },
  {
    label: "Inground Pool Services",
    href: "/services/inground-pools",
  },
  {
    label: "Above Ground Pools",
    href: "/services/above-ground-pools",
  },
  {
    label: "Pool Stairs",
    href: "/services/pool-stairs",
  },
  {
    label: "Spa Covers",
    href: "/services/spa-covers",
  },
  {
    label: "Automatic Pool Cleaners",
    href: "/services/automatic-pool-cleaners",
  },
  {
    label: "Pool Heaters",
    href: "/services/pool-heaters",
  },
  {
    label: "Pool Salt Chlorine Generators",
    href: "/services/pool-salt-chlorine-generators",
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
      <header className="sticky top-0 z-50">
      {/* Main nav - sticky when scrolled */}
      <nav
        className={`bg-white border-b border-gray-100 transition-all duration-300 ${
                  scrolled ? "shadow-md" : ""
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 flex items-center justify-between h-[72px]">
          {/* Logo */}
          <Logo variant="dark" size="md" />

          {/* Desktop menu */}
          <div className="hidden lg:flex items-center gap-0">
            <Link
              to="/"
              className={`px-3 py-2 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${
                isActive("/") && location.pathname === "/"
                  ? "text-[#1e2a5e]"
                  : "text-gray-700 hover:text-[#1e2a5e]"
              }`}
            >
              Home
            </Link>
            <Link
              to="/about"
              className={`px-3 py-2 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${
                isActive("/about")
                  ? "text-[#1e2a5e]"
                  : "text-gray-700 hover:text-[#1e2a5e]"
              }`}
            >
              About Us
            </Link>

            {/* Services dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <Link
                to="/services"
                className={`px-3 py-2 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap flex items-center gap-0.5 ${
                  isActive("/services")
                    ? "text-[#1e2a5e]"
                    : "text-gray-700 hover:text-[#1e2a5e]"
                }`}
              >
                Services
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </Link>
              {servicesOpen && (
                <div className="absolute top-full left-0 bg-white shadow-xl border border-gray-100 min-w-[320px] py-2 z-50">
                  {servicesMenu.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#1e2a5e] transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/contact"
              className={`px-3 py-2 text-sm font-medium uppercase tracking-wide transition-colors whitespace-nowrap ${
                isActive("/contact")
                  ? "text-[#1e2a5e]"
                  : "text-gray-700 hover:text-[#1e2a5e]"
              }`}
            >
              Contact
            </Link>
          </div>

          {/* Call Now button - desktop, visible in main nav */}
          <a
            href="tel:360-206-7120"
            className="hidden lg:inline-flex bg-[#1e2a5e] text-white text-sm font-bold px-5 py-2.5 items-center gap-2 hover:bg-[#152045] transition-colors whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-sm"></i>
            </span>
            Call Now
          </a>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <span
              className={`block w-6 h-0.5 bg-[#1e2a5e] transition-all ${
                mobileOpen ? "rotate-45 translate-y-2" : ""
              }`}
            />
            <span
              className={`block w-6 h-0.5 bg-[#1e2a5e] transition-all ${
                mobileOpen ? "opacity-0" : ""
              }`}
            />
            <span
              className={`block w-6 h-0.5 bg-[#1e2a5e] transition-all ${
                mobileOpen ? "-rotate-45 -translate-y-2" : ""
              }`}
            />
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-1">
            <Link
              to="/"
              className="block py-2 text-sm font-medium text-gray-700 uppercase"
            >
              Home
            </Link>
            <Link
              to="/about"
              className="block py-2 text-sm font-medium text-gray-700 uppercase"
            >
              About Us
            </Link>
            <button
              className="w-full text-left py-2 text-sm font-medium text-gray-700 uppercase flex items-center justify-between"
              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
            >
              Services
              <span className="w-4 h-4 flex items-center justify-center">
                <i
                  className={`ri-arrow-down-s-line text-xs transition-transform ${
                    mobileServicesOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </span>
            </button>
            {mobileServicesOpen && (
              <div className="pl-4 space-y-1">
                {servicesMenu.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="block py-1.5 text-xs text-gray-600"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
            <Link
              to="/contact"
              className="block py-2 text-sm font-medium text-gray-700 uppercase"
            >
              Contact
            </Link>
            <a
              href="tel:360-206-7120"
              className="block py-2 text-sm font-bold text-[#1e2a5e] uppercase"
            >
              (360) 206-7120
            </a>
          </div>
        )}
      </nav>
    </header>
  );
}